import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import SoundService from '../../utils/sounds';
import Spinner from '../ui/Spinner';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';

const UserProfileModal = ({ userId, onClose, currentUser, onShowGame }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bio, setBio] = useState('');
  const [discord, setDiscord] = useState('');
  const [twitch, setTwitch] = useState('');
  const [twitter, setTwitter] = useState('');

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/users/${userId}/profile`);
      setProfile(data);
      setBio(data.bio || '');
      setDiscord(data.discord_username || '');
      setTwitch(data.twitch_username || '');
      setTwitter(data.twitter_username || '');
    } catch (err) { console.error('Failed to load profile:', err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/profile', { bio, discord_username: discord, twitch_username: twitch, twitter_username: twitter });
      SoundService.success();
      setEditMode(false);
      loadProfile();
    } catch (err) { alert('Failed to save: ' + err.message); }
    finally { setSaving(false); }
  };

  const formatJoinDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', width: '95%', maxWidth: '500px', maxHeight: '85vh', overflow: 'auto', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }}
        onClick={e => e.stopPropagation()}
      >
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center' }}><Spinner size={32} /></div>
        ) : profile ? (
          <>
            {/* Header */}
            <div style={{ position: 'relative', padding: '24px', background: `linear-gradient(135deg, ${profile.color}30, transparent)`, borderBottom: `1px solid ${THEME.glassBorder}` }}>
              <motion.button whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }} whileTap={{ scale: 0.95 }} onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, color: THEME.textMuted, width: '36px', height: '36px', borderRadius: '10px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</motion.button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Avatar src={profile.avatar_url} name={profile.display_name} size={72} color={profile.color} />
                <div>
                  <h2 style={{ margin: '0 0 4px', color: profile.color, fontSize: '24px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {profile.display_name}
                    {profile.is_founder && <span style={{ fontSize: '20px' }}>{profile.founder_icon || '⭐'}</span>}
                  </h2>
                  <div style={{ color: THEME.textMuted, fontSize: '14px' }}>@{profile.username}</div>
                  <div style={{ color: THEME.textMuted, fontSize: '12px', marginTop: '4px' }}>Joined {formatJoinDate(profile.created_at)}</div>
                </div>
              </div>
              {profile.bio && !editMode && (
                <p style={{ margin: '16px 0 0', color: THEME.text, fontSize: '14px', lineHeight: '1.5' }}>{profile.bio}</p>
              )}
            </div>

            {/* Edit Section (for own profile) */}
            {profile.is_self && (
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${THEME.border}` }}>
                {editMode ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', color: THEME.textMuted, fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase' }}>Bio</label>
                      <textarea value={bio} onChange={e => setBio(e.target.value)} maxLength={500} rows={2} placeholder="Tell us about yourself..." style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px', color: THEME.text, fontSize: '13px', resize: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <label style={{ display: 'block', color: THEME.textMuted, fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase' }}>Discord</label>
                        <input type="text" value={discord} onChange={e => setDiscord(e.target.value)} placeholder="username" style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px', color: THEME.text, fontSize: '13px', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', color: THEME.textMuted, fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase' }}>Twitch</label>
                        <input type="text" value={twitch} onChange={e => setTwitch(e.target.value)} placeholder="username" style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px', color: THEME.text, fontSize: '13px', boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={handleSave} disabled={saving} style={{ flex: 1, background: profile.color, color: '#000', border: 'none', borderRadius: '8px', padding: '10px', fontWeight: '600', cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save'}</button>
                      <button onClick={() => setEditMode(false)} style={{ background: THEME.bg, color: THEME.textMuted, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setEditMode(true)} style={{ width: '100%', background: THEME.bg, color: THEME.text, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px', fontWeight: '500', cursor: 'pointer' }}>Edit Profile</button>
                )}
              </div>
            )}

            {/* Social Links */}
            {(profile.discord_username || profile.twitch_username || profile.twitter_username || profile.has_steam) && !editMode && (
              <div style={{ padding: '16px 24px', display: 'flex', gap: '10px', flexWrap: 'wrap', borderBottom: `1px solid ${THEME.border}` }}>
                {profile.has_steam && profile.steam_id && (
                  <a href={`https://steamcommunity.com/profiles/${profile.steam_id}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: '#1b2838', borderRadius: '8px', textDecoration: 'none', color: '#fff', fontSize: '13px' }}>
                    <span>🎮</span> Steam
                  </a>
                )}
                {profile.discord_username && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: '#5865F2', borderRadius: '8px', color: '#fff', fontSize: '13px' }}>
                    <span>💬</span> {profile.discord_username}
                  </div>
                )}
                {profile.twitch_username && (
                  <a href={`https://twitch.tv/${profile.twitch_username}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: '#9146FF', borderRadius: '8px', textDecoration: 'none', color: '#fff', fontSize: '13px' }}>
                    <span>📺</span> {profile.twitch_username}
                  </a>
                )}
                {profile.twitter_username && (
                  <a href={`https://twitter.com/${profile.twitter_username}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: '#1DA1F2', borderRadius: '8px', textDecoration: 'none', color: '#fff', fontSize: '13px' }}>
                    <span>𝕏</span> @{profile.twitter_username}
                  </a>
                )}
              </div>
            )}

            {/* Stats */}
            <div style={{ padding: '16px 24px', borderBottom: `1px solid ${THEME.border}` }}>
              <h3 style={{ margin: '0 0 12px', color: THEME.text, fontSize: '14px', fontWeight: '600' }}>Stats</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <div style={{ textAlign: 'center', padding: '12px', background: THEME.bg, borderRadius: '10px' }}>
                  <div style={{ color: profile.color, fontSize: '22px', fontWeight: '700' }}>{profile.stats.games_played}</div>
                  <div style={{ color: THEME.textMuted, fontSize: '11px' }}>Played</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: THEME.bg, borderRadius: '10px' }}>
                  <div style={{ color: profile.color, fontSize: '22px', fontWeight: '700' }}>{profile.stats.total_playtime_hours}h</div>
                  <div style={{ color: THEME.textMuted, fontSize: '11px' }}>Playtime</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: THEME.bg, borderRadius: '10px' }}>
                  <div style={{ color: profile.color, fontSize: '22px', fontWeight: '700' }}>{profile.stats.avg_rating || '—'}</div>
                  <div style={{ color: THEME.textMuted, fontSize: '11px' }}>Avg Rating</div>
                </div>
              </div>
              {!profile.is_self && profile.stats.games_in_common > 0 && (
                <div style={{ marginTop: '12px', padding: '10px', background: `${profile.color}15`, borderRadius: '8px', textAlign: 'center' }}>
                  <span style={{ color: THEME.text, fontSize: '13px' }}>🎮 <strong>{profile.stats.games_in_common}</strong> games in common</span>
                </div>
              )}
            </div>

            {/* Top Played */}
            {profile.top_played?.length > 0 && (
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${THEME.border}` }}>
                <h3 style={{ margin: '0 0 12px', color: THEME.text, fontSize: '14px', fontWeight: '600' }}>Most Played</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {profile.top_played.slice(0, 5).map(game => (
                    <div key={game.id} onClick={() => onShowGame?.(game.id)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', background: THEME.bg, borderRadius: '8px', cursor: onShowGame ? 'pointer' : 'default' }}>
                      <img src={game.header_image} alt="" style={{ width: '48px', height: '22px', objectFit: 'cover', borderRadius: '4px' }} />
                      <span style={{ flex: 1, color: THEME.text, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.name}</span>
                      <span style={{ color: THEME.textMuted, fontSize: '12px' }}>{game.hours}h</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shared Squads */}
            {!profile.is_self && profile.shared_squads?.length > 0 && (
              <div style={{ padding: '16px 24px' }}>
                <h3 style={{ margin: '0 0 12px', color: THEME.text, fontSize: '14px', fontWeight: '600' }}>Shared Squads</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {profile.shared_squads.map(squad => (
                    <div key={squad.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: THEME.bg, borderRadius: '8px' }}>
                      <span>{squad.icon}</span>
                      <span style={{ color: THEME.text, fontSize: '13px' }}>{squad.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '60px', textAlign: 'center', color: THEME.textMuted }}>Profile not found</div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default UserProfileModal;
